
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { CourseBasicFields } from "./CourseBasicFields";
import { CourseDescriptionField } from "./CourseDescriptionField";
import { CourseBenefitsField } from "./CourseBenefitsField";
import { CourseFooterTextField } from "./CourseFooterTextField";
import { CourseDeleteButton } from "./CourseDeleteButton";

export type CourseDataType = {
  id: string;
  course_name: string;
  course_type: string;
  description: string;
  duration_hours: number;
  price: number;
  original_price: number | null;
  state_fee: number | null;
  language: string;
  state?: string | null;
  benefits?: any;
  footer_text?: any;
  created_at?: string;
  updated_at?: string;
};

type CourseFormProps = {
  course: CourseDataType;
  onDeleted?: () => void;
};

export function CourseForm({ course, onDeleted }: CourseFormProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(course);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // Update formData whenever course prop changes
  useEffect(() => {
    console.log("Course data updated:", course);
    setFormData(course);
  }, [course]);

  const validateJSON = (value: string) => {
    if (!value) return true;
    try {
      JSON.parse(value);
      return true;
    } catch (e) {
      toast.error("Invalid JSON format. Please check your JSON syntax.");
      return false;
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      
      // Log form data for debugging
      console.log("Form data before processing:", formData);
      
      // Process JSON strings if changed
      let processedBenefits = formData.benefits;
      let processedFooterText = formData.footer_text;
      
      // If benefits was edited as a string, parse it
      if (typeof formData.benefits === 'string') {
        if (!validateJSON(formData.benefits)) return;
        processedBenefits = formData.benefits ? JSON.parse(formData.benefits) : null;
      }
      
      // If footer_text was edited as a string, parse it
      if (typeof formData.footer_text === 'string') {
        if (!validateJSON(formData.footer_text)) return;
        processedFooterText = formData.footer_text ? JSON.parse(formData.footer_text) : null;
      }

      console.log("Saving course with benefits:", processedBenefits);
      console.log("Saving course with footer text:", processedFooterText);
      console.log("Saving course with state:", formData.state);

      // Use the RPC function to update the course
      const { data, error } = await supabase.rpc('update_course_offering', {
        p_course_id: course.id,
        p_course_name: formData.course_name,
        p_course_type: formData.course_type,
        p_description: formData.description,
        p_duration_hours: formData.duration_hours,
        p_price: formData.price,
        p_original_price: formData.original_price,
        p_state_fee: formData.state_fee,
        p_language: formData.language,
        p_benefits: processedBenefits,
        p_footer_text: processedFooterText,
        p_state: formData.state
      });

      if (error) {
        console.error("Error from Supabase RPC:", error);
        throw error;
      }

      toast.success("Course details updated successfully");
      setIsEditing(false);
    } catch (error: any) {
      console.error("Failed to update course details:", error);
      
      // More specific error handling
      let errorMessage = 'Failed to update course details';
      
      if (error?.message) {
        // Clean up Supabase error messages for better user experience
        const message = error.message;
        if (message.includes('Permission denied')) {
          errorMessage = 'Permission denied: Only admins can update courses';
        } else {
          // Use the original message but clean it up
          errorMessage = message.replace(/^.*?:/, '').trim();
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-2xl font-bold">Course Details</CardTitle>
        <div className="space-x-2">
          {!isEditing ? (
            <Button variant="pill" onClick={() => setIsEditing(true)}>Edit</Button>
          ) : (
            <div className="space-x-2 flex items-center">
              <Button 
                variant="pill" 
                className="bg-white text-black border border-black hover:bg-black/10 dark:bg-black dark:text-white dark:border-white dark:hover:bg-white/10"
                onClick={() => setIsEditing(false)}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button 
                variant="pill" 
                onClick={handleSave} 
                disabled={isSaving}
              >
                {isSaving ? "Saving..." : "Save"}
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      
      {isEditing && (
        <div className="px-6 pb-4">
          <CourseDeleteButton 
            courseId={course.id} 
            isOpen={isDeleteDialogOpen}
            onOpenChange={setIsDeleteDialogOpen}
            onDeleted={onDeleted}
          />
        </div>
      )}
      
      <CardContent className="space-y-4">
        {/* Basic information fields */}
        <CourseBasicFields 
          formData={formData} 
          setFormData={setFormData} 
          isEditing={isEditing} 
        />
        
        {/* Description field */}
        <CourseDescriptionField 
          description={formData.description}
          onChange={(value) => setFormData({ ...formData, description: value })}
          isEditing={isEditing} 
        />

        {/* Benefits field */}
        <CourseBenefitsField 
          benefits={formData.benefits}
          onChange={(value) => setFormData({ ...formData, benefits: value })}
          isEditing={isEditing} 
        />

        {/* Footer text field */}
        <CourseFooterTextField 
          footerText={formData.footer_text}
          onChange={(value) => setFormData({ ...formData, footer_text: value })}
          isEditing={isEditing} 
        />
      </CardContent>
    </Card>
  );
}
